import {merge} from '#core/utils/config-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';

import {generateBorder} from '#core/application/modules/tiles/border/index.js';
import {generateEventCardMarketsSlider} from '#core/application/modules/tiles/event-card-markets-slider/index.js';
import {
    generateAvantgardeEventCardInplayScoreAccent,
    generateAvantgardeOddsButton,
} from '#core/application/modules/cards-and-promotions/avantgarde-cards/avantgarde-event-card-common.js';

const LAYER_BORDER_RADIUS = 18;

export function avantgardeVerticalMatchCardsFactory(PALETTE) {
    return {
        VerticalMatchEventCard: {
            cardWrapper: {
                ...generateBorder(PALETTE.VERTICAL_MATCH_CARD_BORDER),
            },
            liveCardWrapper: {
                boxShadow: PALETTE.VERTICAL_MATCH_CARD_LIVE_SHADOW,
            },
            cardSurface: {
                sportBackgroundColors: PALETTE.VERTICAL_MATCH_SPORT_ACCENT_COLORS,
                defaultBackgroundColor: PALETTE.VERTICAL_MATCH_SURFACE_DEFAULT_BG,
                backgroundColorSoftener: PALETTE.VERTICAL_MATCH_SURFACE_SOFTENER_BG,
                sportBackgroundColorWeight: '60%',
                gradientOverlay: PALETTE.VERTICAL_MATCH_SURFACE_GRADIENT_OVERLAY_BG,
                style: {
                    borderRadius: PALETTE.VERTICAL_MATCH_CARD_BORDER.BORDER_RADIUS,
                    zIndex: 9,
                },
            },
            photoLayer: {
                style: {
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: LAYER_BORDER_RADIUS,
                    borderWidth: 0,
                },
            },
            backdropOverlays: {
                layer1: {
                    style: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        height: 'auto',
                        borderRadius: LAYER_BORDER_RADIUS,
                        ...resolveBackground(PALETTE.VERTICAL_MATCH_BACKDROP_LAYER1_BG),
                    },
                },
                layer2: {
                    style: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        height: 'auto',
                        borderRadius: LAYER_BORDER_RADIUS,
                        backgroundImage: 'none',
                        ...resolveBackground(PALETTE.VERTICAL_MATCH_BACKDROP_LAYER2_BG),
                    },
                },
            },
            eventCard: {
                container: {
                    style: {
                        borderWidth: 0,
                        borderColor: 'transparent',
                        backgroundColor: 'transparent',
                        zIndex: 99,
                    },
                },
                title: {
                    style: PALETTE.VERTICAL_MATCH_EVENT_DETAILS.EVENT_NAME_TEXT,
                },
            },
            sportCountryIcons: {
                sportIcon: {
                    icon: {
                        style: {
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: PALETTE.VERTICAL_MATCH_ICON_RING_BORDER_COLOR,
                            borderRadius: '50%',
                            fill: PALETTE.VERTICAL_MATCH_EVENT_DETAILS.SPORT_ICON_COLOR,
                        },
                    },
                },
                countryIcon: {
                    icon: {
                        style: {
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: PALETTE.VERTICAL_MATCH_ICON_RING_BORDER_COLOR,
                            borderRadius: '50%',
                        },
                    },
                },
            },
            liveIndicator: {
                container: {
                    style: {
                        ...resolveBackground(PALETTE.VERTICAL_MATCH_LIVE_INDICATOR_BG),
                        borderRadius: 10,
                    },
                },
                dot: {
                    style: {
                        borderRadius: '50%',
                        backgroundColor: PALETTE.VERTICAL_MATCH_LIVE_INDICATOR_TIME_TEXT.color,
                    },
                },
                time: {
                    style: PALETTE.VERTICAL_MATCH_LIVE_INDICATOR_TIME_TEXT,
                },
            },
            streamButton: {
                buttonIcon: {
                    style: {
                        fill: PALETTE.VERTICAL_MATCH_STREAM_ICON_COLOR,
                    },
                },
            },
            scoreboard: merge(generateAvantgardeEventCardInplayScoreAccent(PALETTE.VERTICAL_MATCH_EVENT_DETAILS), {
                scoreboards: {
                    genericInplayScoreboard: {
                        scores: {
                            style: {
                                ...resolveBackground(PALETTE.VERTICAL_MATCH_INPLAY_SCORES_BG),
                                borderRadius: 15,
                            },
                        },
                        style: {
                            score: {
                                backgroundColor: 'transparent',
                                borderRadius: 0,
                                minWidth: 'auto',
                                ...PALETTE.VERTICAL_MATCH_EVENT_DETAILS.SCOREBOARD.SCOREBOARD_ACTIVE_SCORE_TEXT,
                            },
                            scoreSeparator: {
                                color: PALETTE.VERTICAL_MATCH_EVENT_DETAILS.PARTICIPANTS.PARTICIPANTS_SEPARATOR_COLOR,
                            },
                            segmentData: {
                                ...PALETTE.VERTICAL_MATCH_EVENT_DETAILS.SCOREBOARD.SCOREBOARD_TIMING_STATUS
                                    .EVENT_TIMING_LIVE_TEXT,
                            },
                            periodDurations: {
                                ...PALETTE.VERTICAL_MATCH_EVENT_DETAILS.SCOREBOARD.SCOREBOARD_TIMING_STATUS
                                    .EVENT_TIMING_LIVE_TEXT,
                            },
                        },
                    },
                    setBasedInplayScoreboard: {
                        segmentBasedInplayScoreboard: {
                            participantRow: {
                                style: {
                                    display: 'flex',
                                },
                            },
                            eventInfoColumn: {
                                itemSpacing: 4,
                            },
                            segmentColumn: {
                                itemSpacing: 4,
                            },
                            currentSegmentColumn: {
                                itemSpacing: 4,
                            },
                            totalsColumn: {
                                itemSpacing: 4,
                            },
                            serverColumn: {
                                itemSpacing: 4,
                            },
                        },
                    },
                    periodBasedInplayScoreboard: {
                        segmentBasedInplayScoreboard: {
                            participantRow: {
                                style: {
                                    display: 'flex',
                                },
                            },
                            eventInfoColumn: {
                                itemSpacing: 4,
                            },
                            segmentColumn: {
                                itemSpacing: 4,
                            },
                            currentSegmentColumn: {
                                itemSpacing: 4,
                            },
                            totalsColumn: {
                                itemSpacing: 4,
                            },
                            serverColumn: {
                                itemSpacing: 4,
                            },
                        },
                    },
                },
            }),
            marketsSlider: merge(generateEventCardMarketsSlider(PALETTE.VERTICAL_MATCH_MARKETS_SLIDER), {
                marketTitle: {
                    style: PALETTE.VERTICAL_MATCH_MARKET_TITLE_TEXT,
                },
                genericMarketContent: {
                    container: {
                        style: {
                            padding: '0 8px',
                            ...resolveBackground('none'),
                        },
                    },
                    market: {
                        selectionButton: generateAvantgardeOddsButton(PALETTE.VERTICAL_MATCH_ODDS_BUTTON),
                    },
                    marketsContainer: {
                        style: {
                            ...resolveBackground('none'),
                        },
                    },
                },
            }),
        },
    };
}
